import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { updateOranAlarm } from '@api/common';

import { IAlarmOran } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';

import { ESnackbarTypes } from '@enums/common';

import { ACTIVE_ALARMS, ALARM_HISTORY, ALARM_LIST, ALARM_LIST_ORAN, EDIT_ALARM } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';
import { CUSTOM_ALARM_NO_FILE_ID } from '@components/routes/AdminPanel/AlarmManagement/AlarmManagementByVendor/constants';

import { useMutateAlarmsCreateNoFileRules } from './useMutateAlarmsCreateNoFileRules';
import { useMutateAlarmsCreateNoVesRules } from './useMutateAlarmsCreateNoVesRules';
import { useMutateAlarmsDeleteNoFileRules } from './useMutateAlarmsDeleteNoFileRules';
import { useMutateAlarmsDeleteNoVesRules } from './useMutateAlarmsDeleteNoVesRules';
import { useMutateAlarmsUpdateNoFileRules } from './useMutateAlarmsUpdateNoFileRules';
import { useMutateAlarmsUpdateNoVesRules } from './useMutateAlarmsUpdateNoVesRules';

const useCustomAlarm = (customAlarmId?: string) => {
	const { addAlarmNoVesRules, isLoadingAddAlarmNoVesRules } = useMutateAlarmsCreateNoVesRules();
	const { deleteAlarmNoVesRules, isLoadingDeleteAlarmNoVesRules } = useMutateAlarmsDeleteNoVesRules();
	const { updateAlarmNoVesRules, isLoadingUpdateAlarmNoVesRules } = useMutateAlarmsUpdateNoVesRules();
	const { addAlarmNoFileRules, isLoadingAddAlarmNoFileRules } = useMutateAlarmsCreateNoFileRules();
	const { deleteAlarmNoFileRules, isLoadingDeleteAlarmNoFileRules } = useMutateAlarmsDeleteNoFileRules();
	const { updateAlarmNoFileRules, isLoadingUpdateAlarmNoFileRules } = useMutateAlarmsUpdateNoFileRules();

	return {
		addAlarms: customAlarmId === CUSTOM_ALARM_NO_FILE_ID ? addAlarmNoFileRules : addAlarmNoVesRules,
		deleteAlarms: customAlarmId === CUSTOM_ALARM_NO_FILE_ID ? deleteAlarmNoFileRules : deleteAlarmNoVesRules,
		updateAlarms: customAlarmId === CUSTOM_ALARM_NO_FILE_ID ? updateAlarmNoFileRules : updateAlarmNoVesRules,
		isLoading: isLoadingAddAlarmNoVesRules
			|| isLoadingDeleteAlarmNoVesRules
			|| isLoadingUpdateAlarmNoVesRules
			|| isLoadingAddAlarmNoFileRules
			|| isLoadingDeleteAlarmNoFileRules
			|| isLoadingUpdateAlarmNoFileRules,
	};
};

export const useMutateUpdateOranAlarm = (onSuccess: () => void, customAlarmId?: string) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		addAlarms,
		deleteAlarms,
		updateAlarms,
		isLoading,
	} = useCustomAlarm(customAlarmId);

	const {
		mutate: editOranAlarm,
		isLoading: isLoadingEditOranAlarm,
	} = useMutation<IAlarmSettingsDTO>({
		mutationKey: [EDIT_ALARM],
		mutationFn: updateOranAlarm,
		onSuccess: (data: IAlarmOran) => {
			dispatch(setSnackbar({
				message: responseMessages.alarmOranUpdated(data.name),
				type: ESnackbarTypes.success,
			}));

			if (onSuccess) {
				onSuccess();
			}

			return [
				queryClient.invalidateQueries([ALARM_LIST]),
				queryClient.invalidateQueries([ACTIVE_ALARMS]),
				queryClient.invalidateQueries([ALARM_HISTORY]),
				queryClient.invalidateQueries([ALARM_LIST_ORAN]),
			];
		},
	});

	const updateOranAlarmSettings = async (dto: IAlarmSettingsDTO, alarm?: IAlarmOran) => {
		const { networkElementFields, ...rest } = dto;
		if (networkElementFields) {
			const isNoFileAlarm = customAlarmId === CUSTOM_ALARM_NO_FILE_ID;
			const customSettings = isNoFileAlarm
				? alarm?.customSettings?.noFileAlarmCreateSettings.map(
					item => ({ ...item, noEventMinutes: item.noFileMinutes }),
				) || []
				: alarm?.customSettings?.noVesEventAlarmCreateSettings || [];

			const fieldsToAdd = networkElementFields.filter((field) => !field?.id);

			const fieldsToDelete = customSettings
				.filter((field) => {
					return field?.id && !networkElementFields.find((setting) => setting.id === field.id);
				}).map((field) => ({ id: field.id }));

			const fieldsToUpdate = networkElementFields.filter((field) => {
				return field?.id && customSettings.find((setting) => {
					return setting.id === field.id && setting.noEventMinutes !== field.noEventMinutes;
				});
			})?.map((field) => ({ id: field.id, noEventMinutes: field.noEventMinutes }));

			if (fieldsToDelete.length) await deleteAlarms(fieldsToDelete);
			if (fieldsToUpdate.length) await updateAlarms(fieldsToUpdate);
			if (fieldsToAdd.length) await addAlarms(fieldsToAdd);
		}

		editOranAlarm(rest);
	};

	return {
		editOranAlarm: updateOranAlarmSettings,
		isLoadingEditOranAlarm: isLoadingEditOranAlarm || isLoading,
	};
};
